import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Search, FileDown } from "lucide-react";
import { Section, Empty } from "./CommonUI";
import { INR_P, fmtDate } from "./clientUtils";

export default function ReceiptsTable({ rows, loading, onRefresh, token, financeToken }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => rows.filter((r) => {
    if (!q) return true;
    return (`${r.bank_account_name} ${r.notes || ""}`).toLowerCase().includes(q.toLowerCase());
  }), [rows, q]);

  const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

  const downloadReceipt = async (rec) => {
    try {
      const url = `${apiBase}/client/payment-receipts/${rec.id}/file`;
      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Finance-Token": financeToken,
        },
      });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = rec?.attachment?.filename || `receipt-${(rec.id || "").slice(0, 6)}`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch {
      toast.error("Failed to download receipt");
    }
  };

  const downloadAutoPdf = async (rec) => {
    try {
      const url = `${apiBase}/client/payment-receipts/${rec.id}/pdf`;
      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Finance-Token": financeToken,
        },
      });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `Receipt_${rec.reference_number || (rec.id || "").slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch {
      toast.error("Failed to generate receipt PDF");
    }
  };

  return (
    <Section title="Payment Receipts" subtitle="Every payment receipt logged against your account." onRefresh={onRefresh} loading={loading}
      toolbar={
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#9a9a9a]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search bank / notes…"
            data-testid="client-receipts-search"
            className="font-mono text-xs border border-black/15 rounded-full pl-8 pr-3 py-1.5 w-56 outline-none focus:border-[#ff5722] focus:ring-2 focus:ring-[#ff5722]/20"
          />
        </div>
      }>
      {filtered.length === 0 ? (
        <Empty msg={rows.length === 0 ? "No payment receipts on your account yet." : "No receipts match your search."} />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left min-w-[900px]">
            <thead className="bg-[#f7f6f3] border-b border-black/10 text-[11px] font-mono uppercase tracking-[0.2em] text-[#4a4a4a]">
              <tr>
                <th className="px-4 py-3 w-12">S.no</th>
                <th className="px-4 py-3">Received date</th>
                <th className="px-4 py-3">Bank account</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3">Notes</th>
                <th className="px-4 py-3 text-right">Receipt</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => (
                <tr key={r.id} data-testid={`client-receipt-${r.id}`} className="border-b border-black/5 hover:bg-[#0a0a0a]/[0.02]">
                  <td className="px-4 py-4 text-xs font-mono text-[#4a4a4a]">{String(i + 1).padStart(2, "0")}</td>
                  <td className="px-4 py-4 text-xs font-mono">{fmtDate(r.received_date)}</td>
                  <td className="px-4 py-4 text-sm font-semibold text-[#0a0a0a]">{r.bank_account_name || "—"}</td>
                  <td className="px-4 py-4 text-right font-mono font-bold text-emerald-700">{INR_P(r.amount)}</td>
                  <td className="px-4 py-4 text-sm text-[#4a4a4a] max-w-[28ch] truncate">{r.notes || "—"}</td>
                  <td className="px-4 py-4 text-right">
                    <div className="inline-flex items-center gap-1.5">
                      <button onClick={() => downloadAutoPdf(r)} data-testid={`client-receipt-pdf-${r.id}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs border border-[#ff5722] bg-[#ff5722] text-white rounded-full hover:bg-[#0a0a0a] hover:border-[#0a0a0a] transition-colors">
                        <FileDown className="h-3.5 w-3.5" /> PDF
                      </button>
                      {r.has_attachment && (
                        <button onClick={() => downloadReceipt(r)} data-testid={`client-receipt-download-${r.id}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs border border-black/15 rounded-full hover:border-[#ff5722] hover:text-[#ff5722]"
                          title="Manually uploaded attachment">
                          <FileDown className="h-3.5 w-3.5" /> File
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}
